import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import i18n from '../i18n';
import { loadTranslationOverrides } from '../i18n';
import { useAuth } from './AuthContext';

// Singleton locale context for use in pure functions (formatters.js)
let _localeContext = {
  language: 'es',
  icu_locale: 'es-AR',
  primary_currency: 'ARS',
  secondary_currency: null,
};

/**
 * Get current locale context (singleton).
 * Used by formatters.js and other pure utility functions that can't use React hooks.
 */
export function getLocaleContext() {
  return _localeContext;
}

const LocaleContext = createContext();

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

export const LocaleProvider = ({ children }) => {
  const [locale, setLocale] = useState(_localeContext);
  const { user } = useAuth();
  const lastLanguageRef = useRef(null);

  /**
   * Update locale from user data (called when user logs in or changes language).
   * Accepts user object with preferred_language and biller/publisher country info.
   */
  const updateLocaleFromUser = useCallback((userData) => {
    if (!userData) return;

    const language = userData.preferred_language || 'es';

    // Determine ICU locale from country data or fallback based on language
    let icu_locale = 'es-AR'; // default
    let primary_currency = 'ARS';
    let secondary_currency = null;

    if (userData.country) {
      // Country data from biller/publisher
      const country = userData.country;
      // Convert underscore locale to hyphen for Intl API (es_AR -> es-AR)
      icu_locale = (country.icu_locale || 'es_AR').replace('_', '-');
      primary_currency = country.primary_currency || 'ARS';
      secondary_currency = country.secondary_currency || null;
    } else {
      // Fallback based on language preference
      const localeFallbacks = {
        es: 'es-AR',
        en: 'en-US',
        pt: 'pt-BR',
      };
      icu_locale = localeFallbacks[language] || 'es-AR';

      const currencyFallbacks = {
        es: 'ARS',
        en: 'USD',
        pt: 'BRL',
      };
      primary_currency = currencyFallbacks[language] || 'ARS';
    }

    const newLocale = { language, icu_locale, primary_currency, secondary_currency };

    // Update singleton for pure functions
    _localeContext = newLocale;

    // Update React state
    setLocale(newLocale);

    // Sync i18next language
    if (i18n.language !== language) {
      i18n.changeLanguage(language);
    }
  }, []);

  /**
   * Change language explicitly (from Profile preferences selector).
   * Recalculates icu_locale and primary_currency based on the new language.
   */
  const changeLanguage = useCallback(async (newLanguage) => {
    const validLanguages = ['es', 'en', 'pt'];
    if (!validLanguages.includes(newLanguage)) return;

    // Update i18next
    await i18n.changeLanguage(newLanguage);

    // Load custom overrides from backend
    await loadTranslationOverrides(newLanguage);

    // Recalculate icu_locale and primary_currency for formatters
    const localeFallbacks = {
      es: 'es-AR',
      en: 'en-US',
      pt: 'pt-BR',
    };
    const currencyFallbacks = {
      es: 'ARS',
      en: 'USD',
      pt: 'BRL',
    };

    const newLocale = {
      language: newLanguage,
      icu_locale: localeFallbacks[newLanguage] || 'es-AR',
      primary_currency: currencyFallbacks[newLanguage] || 'ARS',
      secondary_currency: locale.secondary_currency,
    };

    _localeContext = newLocale;
    setLocale(newLocale);
  }, [locale]);

  // Sync language when user authenticates or preferred_language changes
  useEffect(() => {
    const userLang = user?.preferred_language || 'es';
    if (user && userLang !== lastLanguageRef.current) {
      lastLanguageRef.current = userLang;
      updateLocaleFromUser(user);
      loadTranslationOverrides(userLang);
    }
  }, [user, updateLocaleFromUser]);

  // Load translation overrides on mount for the current language
  useEffect(() => {
    loadTranslationOverrides(locale.language);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const value = {
    ...locale,
    changeLanguage,
    updateLocaleFromUser,
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export default LocaleContext;
